import React, { JSX } from 'react'
import { fitToObjectFit } from 'components/BackgroundImageInput'
import { CenteredOnCanvas } from 'components/ScaledStage'
import { TWITCH_MARK_SRC, YOUTUBE_MARK_SRC } from 'components/chatOverlays/platformMarks'
import { SourceMarkLink, usePageSourceMarkLinks } from 'components/chatOverlays/sourceMarkLinks'
import { voteMarkTarget } from 'lib/sourceMarkUrls'
import { POLL, elementFontStack, elementPinnedWeight } from 'lib/elementCanvas'
import { ChatFontKey, normalizeChatFontKey } from 'lib/chatFonts'
import { useFontGate } from 'lib/fontLoader'
import { DEFAULT_TRIVIA_GRACE_SECONDS } from 'lib/triviaPhase'
import {
  VoteLaneMode,
  largestRemainderPercents,
  sumCounts,
  twitchChannelCountOf,
  voteLaneModeOf
} from 'lib/votePercent'
import { ImageFit, TriviaSummary, VoteCountsByPlatform } from 'types/api'

const DEFAULT_BORDER_WIDTH = 0
const DEFAULT_BORDER_COLOR = '#00000000'
const DEFAULT_WINNER_OUTLINE_COLOR = '#67E8F9'

const PILL_FONT_SIZE = 16
const PILL_ICON_SIZE = 13
const round2 = (x: number): number => Math.round(x * 100) / 100
const scaleBy = (px: number, base: number, baseFont: number): number => round2((px * base) / baseFont)

export const triviaTextSizeDefaults = (): {
  question: number
  displayText: number
  command: number
  number: number
  callToAction: number
  revealText: number
} => ({
  question: POLL.heading.fontSize,
  displayText: POLL.option.fontSize,
  command: POLL.option.fontSize,
  number: POLL.option.fontSize,
  callToAction: POLL.callToAction.fontSize,
  revealText: PILL_FONT_SIZE
})

const rowSpanSize = (px: number | null | undefined): { fontSize?: string, lineHeight?: string } => px == null
  ? {}
  : { fontSize: `${px}px`, lineHeight: `${scaleBy(px, POLL.option.lineHeight, POLL.option.fontSize)}px` }

const pillSize = (px: number | undefined): React.CSSProperties => px == null
  ? {}
  : { fontSize: `${px}px`, whiteSpace: 'normal', overflowWrap: 'anywhere', maxWidth: '100%', textAlign: 'center' }

export interface TriviaDraft {
  name: string
  question: string
  callToActionText: string
  options: Array<{
    displayText: string
    command: string
    textColor: string
    progressBarColor: string
    backgroundColor: string
  }>
  headingTextColor: string
  headingBackground: string
  optionsTextColor: string
  optionsBackground: string
  callToActionTextColor: string
  callToActionBackground: string
  alternatingOptionBackground: string
  backgroundImage: string
  backgroundImageFit: ImageFit
  cornerRadius: number
  borderWidth: number
  borderColor: string
  winnerOutlineColor: string
  fontKey: ChatFontKey
  hideLiveResults: boolean
  showAnswersCounter: boolean
  closeGraceSeconds: number
  correctOptionIndex: number
  questionFontSize?: number | null
  displayTextFontSize?: number | null
  commandFontSize?: number | null
  numberFontSize?: number | null
  callToActionFontSize?: number | null
  revealTextFontSize?: number | null
}

export interface TriviaLiveData {
  totalVotes: number
  optionVoteCounts: number[]
  optionVoteCountsByPlatform?: VoteCountsByPlatform[]
  hasYouTubeVotes?: boolean
  laneMode?: VoteLaneMode
  closed: boolean
  revealed: boolean
  hidden: boolean
  answersIn: VoteCountsByPlatform | null
  correctIndex: number
  youtubeRemoved: boolean
}

export const draftFromTrivia = (trivia: TriviaSummary): TriviaDraft => ({
  name: trivia.name,
  question: trivia.question,
  callToActionText: trivia.callToActionText ?? '',
  options: trivia.options.map((o) => ({
    displayText: o.displayText,
    command: o.command,
    textColor: o.textColor,
    progressBarColor: o.progressBarColor,
    backgroundColor: o.backgroundColor ?? ''
  })),
  headingTextColor: trivia.headingTextColor,
  headingBackground: trivia.headingBackground,
  optionsTextColor: trivia.optionsTextColor,
  optionsBackground: trivia.optionsBackground,
  callToActionTextColor: trivia.callToActionTextColor,
  callToActionBackground: trivia.callToActionBackground,
  alternatingOptionBackground: trivia.alternatingOptionBackground ?? trivia.optionsBackground,
  backgroundImage: trivia.backgroundImage ?? '',
  backgroundImageFit: trivia.backgroundImageFit ?? 'COVER',
  cornerRadius: trivia.cornerRadius,
  borderWidth: trivia.borderWidth ?? DEFAULT_BORDER_WIDTH,
  borderColor: trivia.borderColor ?? DEFAULT_BORDER_COLOR,
  winnerOutlineColor: trivia.winnerOutlineColor ?? DEFAULT_WINNER_OUTLINE_COLOR,
  fontKey: normalizeChatFontKey(trivia.fontKey ?? 'DEFAULT'),
  hideLiveResults: trivia.hideLiveResults === true,
  showAnswersCounter: trivia.showAnswersCounter !== false,
  closeGraceSeconds: trivia.closeGraceSeconds ?? DEFAULT_TRIVIA_GRACE_SECONDS,
  correctOptionIndex: trivia.options.findIndex((o) => o.isCorrect === true),
  questionFontSize: trivia.questionFontSize ?? null,
  displayTextFontSize: trivia.displayTextFontSize ?? null,
  commandFontSize: trivia.commandFontSize ?? null,
  numberFontSize: trivia.numberFontSize ?? null,
  callToActionFontSize: trivia.callToActionFontSize ?? null,
  revealTextFontSize: trivia.revealTextFontSize ?? null
})

export const triviaLiveFromSummary = (trivia: TriviaSummary): TriviaLiveData => {
  const byPlatform = trivia.options.map(
    (o) => o.voteCountByPlatform ?? { twitch: o.voteCount, youtube: 0 }
  )
  const youtubeCounts = byPlatform.map((c) => c.youtube)
  const answersIn = trivia.answersIn ?? (
    trivia.hideLiveResults === true && trivia.revealedAt == null && trivia.showAnswersCounter === true
      ? {
          twitch: trivia.options.reduce((s, o) => s + (o.voteCountByPlatform?.twitch ?? o.voteCount), 0),
          youtube: trivia.options.reduce((s, o) => s + (o.voteCountByPlatform?.youtube ?? 0), 0)
        }
      : null
  )
  const laneMode = voteLaneModeOf({
    twitchChannelCount: twitchChannelCountOf(trivia.broadcasterUserIds),
    twitchTotal: Math.max(sumCounts(byPlatform.map((c) => c.twitch)), answersIn?.twitch ?? 0),
    youtubeTotal: Math.max(sumCounts(youtubeCounts), answersIn?.youtube ?? 0)
  })
  const youtubeOnly = laneMode === 'YOUTUBE'
  return {
    totalVotes: youtubeOnly ? sumCounts(youtubeCounts) : trivia.totalVotes,
    optionVoteCounts: youtubeOnly ? youtubeCounts : trivia.options.map((o) => o.voteCount),
    optionVoteCountsByPlatform: byPlatform,
    hasYouTubeVotes: (trivia.hasYouTubeVotes ?? false) ||
      trivia.options.some((o) => (o.voteCountByPlatform?.youtube ?? 0) > 0),
    closed: trivia.closedAt != null,
    revealed: trivia.revealedAt != null,
    hidden: trivia.hideLiveResults === true && trivia.revealedAt == null,
    answersIn,
    correctIndex: trivia.options.findIndex((o) => o.isCorrect === true),
    youtubeRemoved: trivia.youtubeCountsPurgedAt != null,
    laneMode
  }
}

const TRIVIA_KEYFRAMES = `
@keyframes spTriviaGlint { from { background-position: 200% 0; } to { background-position: -200% 0; } }
@keyframes spTriviaPulse { 0%, 100% { opacity: 0.62; } 50% { opacity: 1; } }
@keyframes spTriviaFadeIn { from { opacity: 0; } to { opacity: 1; } }
`

const LockIcon = ({ size }: { size?: number }): JSX.Element => {
  const edge = size == null ? String(PILL_ICON_SIZE) : String(size)
  return (
    <svg width={edge} height={edge} viewBox='0 0 24 24' fill='none' aria-hidden='true' style={size == null ? { display: 'block' } : { display: 'block', flexShrink: 0 }}>
      <rect x='5' y='11' width='14' height='9' rx='2.5' fill='currentColor' />
      <path d='M8 11V8a4 4 0 0 1 8 0v3' stroke='currentColor' strokeWidth='2.2' fill='none' />
    </svg>
  )
}

const AnswersLockedPill = ({ fontWeight, fontSize }: { fontWeight: number, fontSize?: number }): JSX.Element => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      padding: '7px 18px',
      borderRadius: '9999px',
      background: 'rgba(0,0,0,0.38)',
      border: '1px solid rgba(255,255,255,0.18)',
      color: '#FFFFFF',
      fontWeight,
      fontSize: '16px',
      letterSpacing: '0.07em',
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
      opacity: 0,
      animation: 'spTriviaFadeIn 500ms ease-out forwards',
      ...pillSize(fontSize)
    }}
  >
    <LockIcon size={fontSize == null ? undefined : scaleBy(fontSize, PILL_ICON_SIZE, PILL_FONT_SIZE)} />
    Answers locked
  </span>
)

const RevealPill = ({ live, correctIndex, youtubeRemoved, fontWeight, fontSize }: {
  live: TriviaLiveData
  correctIndex: number
  youtubeRemoved: boolean
  fontWeight: number
  fontSize?: number
}): JSX.Element => {
  const byPlatform = live.optionVoteCountsByPlatform ??
    live.optionVoteCounts.map((c) => ({ twitch: c, youtube: 0 }))
  const twitchAnswered = byPlatform.reduce((s, c) => s + c.twitch, 0)
  const youtubeAnswered = byPlatform.reduce((s, c) => s + c.youtube, 0)
  const twitchCorrect = correctIndex >= 0 ? byPlatform[correctIndex]?.twitch ?? 0 : 0
  const youtubeCorrect = correctIndex >= 0 ? byPlatform[correctIndex]?.youtube ?? 0 : 0
  const youtubeOnly = live.laneMode === 'YOUTUBE'
  const labelled = !youtubeOnly && (youtubeAnswered > 0 || youtubeRemoved)
  const parts: string[] = []
  if (twitchAnswered > 0) {
    const p = Math.round((twitchCorrect / twitchAnswered) * 100)
    parts.push(labelled ? `Twitch ${p}% got it right` : `${p}% got it right`)
  }
  if (youtubeRemoved) {
    parts.push('YouTube removed')
  } else if (youtubeAnswered > 0) {
    const p = Math.round((youtubeCorrect / youtubeAnswered) * 100)
    parts.push(youtubeOnly ? `${p}% got it right` : `YouTube ${p}% got it right`)
  }
  const detail = parts.length > 0 ? parts.join(' · ') : 'No answers'
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '7px 18px',
        borderRadius: '9999px',
        background: 'rgba(0,0,0,0.38)',
        border: '1px solid rgba(255,255,255,0.18)',
        color: '#FFFFFF',
        fontWeight,
        fontSize: '16px',
        letterSpacing: '0.07em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        opacity: 0,
        animation: 'spTriviaFadeIn 500ms ease-out forwards',
        ...pillSize(fontSize)
      }}
    >
      Correct answer • {detail}
    </span>
  )
}

type LanePlatform = 'twitch' | 'youtube'
const LANE_DIM_OPACITY = 0.45
const LANE_YOUTUBE_FILL_OPACITY = 0.6
const SPLIT_LANES = { laneGap: 4, laneMarkHeight: 12, markColumn: 15, markGap: 6, figureMarkHeight: 14 }

const VoteMark = ({ platform, height, gapBefore = 0 }: { platform: LanePlatform, height: number, gapBefore?: number }): JSX.Element => {
  const linked = usePageSourceMarkLinks()
  return (
    <SourceMarkLink target={linked ? voteMarkTarget(platform) : null}>
      <img
        src={platform === 'twitch' ? TWITCH_MARK_SRC : YOUTUBE_MARK_SRC}
        alt={platform === 'twitch' ? 'Twitch' : 'YouTube'}
        data-vote-mark={platform}
        style={{
          display: 'inline-block',
          width: 'auto',
          height: `${height}px`,
          flexShrink: 0,
          marginLeft: gapBefore > 0 ? `${gapBefore}px` : undefined,
          verticalAlign: 'middle',
          filter: 'none',
          opacity: 1,
          transform: 'none',
          mixBlendMode: 'normal'
        }}
      />
    </SourceMarkLink>
  )
}

const dimOf = (dimmed: boolean): React.CSSProperties => ({
  opacity: dimmed ? LANE_DIM_OPACITY : 1,
  transition: 'opacity 350ms ease'
})

const VoteLane = ({ platform, pct, color, glint, dimmed }: {
  platform: LanePlatform
  pct: number
  color: string
  glint: boolean
  dimmed: boolean
}): JSX.Element => (
  <div style={{ display: 'flex', alignItems: 'center', gap: `${SPLIT_LANES.markGap}px` }}>
    <span
      style={{
        flex: `0 0 ${SPLIT_LANES.markColumn}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: `${Math.max(POLL.option.barHeight, SPLIT_LANES.laneMarkHeight)}px`
      }}
    >
      <VoteMark platform={platform} height={SPLIT_LANES.laneMarkHeight} />
    </span>
    <div
      data-lane-track={platform}
      style={{
        flex: '1 1 0',
        minWidth: 0,
        height: `${POLL.option.barHeight}px`,
        borderRadius: '9999px',
        background: 'rgba(255,255,255,0.15)',
        overflow: 'hidden',
        ...dimOf(dimmed)
      }}
    >
      <div
        data-lane-fill={platform}
        style={{
          position: 'relative',
          width: `${pct}%`,
          height: '100%',
          borderRadius: '9999px',
          background: color,
          opacity: platform === 'youtube' ? LANE_YOUTUBE_FILL_OPACITY : 1,
          overflow: 'hidden',
          transition: 'width 180ms ease-out'
        }}
      >
        {glint && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(110deg, transparent 35%, rgba(255,255,255,0.85) 50%, transparent 65%)',
              backgroundSize: '220% 100%',
              backgroundRepeat: 'no-repeat',
              animation: 'spTriviaGlint 2s linear infinite'
            }}
          />
        )}
      </div>
    </div>
  </div>
)

const DimmableBackground = ({ background, dimmed }: { background: string, dimmed: boolean }): JSX.Element => (
  <div
    aria-hidden='true'
    style={{
      position: 'absolute',
      inset: 0,
      zIndex: -1,
      background,
      ...dimOf(dimmed)
    }}
  />
)

const CorrectAnswerGlow = ({ color }: { color: string }): JSX.Element => (
  <div
    style={{
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      opacity: 0,
      animation: 'spTriviaFadeIn 500ms ease-out forwards'
    }}
  >
    <div
      style={{
        position: 'absolute',
        inset: '3px 12px',
        borderRadius: '16px',
        border: `2.5px solid ${color}`,
        boxShadow: `0 0 16px ${color}, 0 0 5px ${color}, inset 0 0 12px ${color}55`,
        animation: 'spTriviaPulse 2.2s ease-in-out infinite'
      }}
    />
    <div
      style={{
        position: 'absolute',
        inset: '3px 12px',
        borderRadius: '16px',
        padding: '2.5px',
        background: 'linear-gradient(110deg, transparent 38%, rgba(255,255,255,0.92) 50%, transparent 62%)',
        backgroundSize: '220% 100%',
        backgroundRepeat: 'no-repeat',
        animation: 'spTriviaGlint 2.6s linear infinite',
        WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
        WebkitMaskComposite: 'xor',
        mask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
        maskComposite: 'exclude'
      }}
    />
  </div>
)

export const TriviaPreview = ({
  draft,
  live,
  layoutWidth = POLL.width
}: {
  draft: TriviaDraft
  live?: TriviaLiveData
  layoutWidth?: number
}): JSX.Element => {
  const fontFamily = elementFontStack(draft.fontKey)
  const fontReady = useFontGate(draft.fontKey)
  const hasBgImage = draft.backgroundImage.trim().length > 0
  const barWidth = Math.max(120, layoutWidth - (POLL.width - POLL.option.barWidth))
  const optionTextMaxWidth = layoutWidth - (POLL.width - POLL.option.textMaxWidth)
  const ctaTextMaxWidth = layoutWidth - (POLL.width - POLL.callToAction.textMaxWidth)
  const singlePcts = live == null ? [] : largestRemainderPercents(live.optionVoteCounts)
  const pctFor = (i: number): number => singlePcts[i] ?? 0
  const altBg = draft.alternatingOptionBackground.trim().length > 0
    ? draft.alternatingOptionBackground
    : draft.optionsBackground
  const laneMode: VoteLaneMode = live == null
    ? 'TWITCH'
    : (live.laneMode ?? (live.hasYouTubeVotes === true ? 'SPLIT' : 'TWITCH'))
  const split = laneMode === 'SPLIT'
  const byPlatform = live?.optionVoteCountsByPlatform ?? []
  const twitchPcts = largestRemainderPercents(split ? draft.options.map((_, i) => byPlatform[i]?.twitch ?? live?.optionVoteCounts[i] ?? 0) : [])
  const youtubePcts = largestRemainderPercents(split ? draft.options.map((_, i) => byPlatform[i]?.youtube ?? 0) : [])
  const hiddenMode = live?.hidden === true
  const answersLocked = live?.closed === true && !live.revealed
  const revealed = live?.revealed === true
  const correctIndex = revealed && live != null ? live.correctIndex : -1
  const q = draft.questionFontSize
  const heading = q == null
    ? POLL.heading
    : { ...POLL.heading, fontSize: q, lineHeight: scaleBy(q, POLL.heading.lineHeight, POLL.heading.fontSize) }
  const counterFontSize = draft.numberFontSize == null
    ? POLL.option.fontSize - 2
    : scaleBy(draft.numberFontSize, POLL.option.fontSize - 2, POLL.option.fontSize)
  const pillFontSize = draft.revealTextFontSize ?? undefined
  const rowAlign = draft.commandFontSize == null && draft.displayTextFontSize == null && (draft.numberFontSize == null || hiddenMode)
    ? 'flex-start'
    : 'baseline'
  if (!fontReady) return <CenteredOnCanvas width={layoutWidth}>{null}</CenteredOnCanvas>
  return (
    <CenteredOnCanvas width={layoutWidth}>
      <div
        data-testid='trivia-preview'
        style={{
          position: 'relative',
          width: '100%',
          fontFamily,
          overflow: 'hidden',
          borderRadius: `${draft.cornerRadius}px`,
          border: `${draft.borderWidth ?? DEFAULT_BORDER_WIDTH}px solid ${draft.borderColor ?? DEFAULT_BORDER_COLOR}`
        }}
      >
        {hasBgImage && (
          <img
            src={draft.backgroundImage}
            alt=''
            aria-hidden='true'
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: fitToObjectFit(draft.backgroundImageFit)
            }}
            onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.2' }}
          />
        )}
        <div style={{ position: 'relative' }}>
          <div
            style={{
              paddingTop: `${POLL.heading.paddingTop}px`,
              paddingBottom: `${POLL.heading.paddingBottom}px`,
              paddingLeft: `${POLL.heading.paddingLeft}px`,
              paddingRight: `${POLL.heading.paddingRight}px`,
              background: draft.headingBackground,
              color: draft.headingTextColor,
              fontWeight: POLL.heading.fontWeight,
              fontSize: `${heading.fontSize}px`,
              lineHeight: `${heading.lineHeight}px`,
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <span>
              {draft.question.length > 0 ? draft.question : 'Your trivia question'}
            </span>
          </div>
          {(revealed || answersLocked) && <style>{TRIVIA_KEYFRAMES}</style>}
          <div>
            {draft.options.map((o, i) => {
              const fallbackBg = i % 2 === 0 ? draft.optionsBackground : altBg
              const rowBg = o.backgroundColor.trim().length > 0 ? o.backgroundColor : fallbackBg
              const pct = pctFor(i)
              const filledFraction = (live != null) ? pct / 100 : 0.08
              const filledWidth = filledFraction * barWidth
              const pctLabel = (live != null) ? `${pct}%` : '0%'
              const countLabel = pctLabel
              if (split && !hiddenMode) {
                const correct = revealed && i === correctIndex
                const dimmed = revealed && correctIndex !== -1 && i !== correctIndex
                return (
                  <div
                    key={i}
                    data-split-option={i}
                    style={{
                      position: 'relative',
                      isolation: 'isolate',
                      color: draft.optionsTextColor,
                      paddingTop: `${POLL.option.paddingTop}px`,
                      paddingBottom: `${POLL.option.paddingBottom}px`,
                      paddingLeft: `${POLL.option.paddingLeft}px`,
                      paddingRight: `${POLL.option.paddingRight}px`
                    }}
                  >
                    <DimmableBackground background={rowBg} dimmed={dimmed} />
                    {correct && <CorrectAnswerGlow color={draft.winnerOutlineColor} />}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: rowAlign,
                        justifyContent: 'space-between',
                        gap: `${POLL.option.chatCommandGap}px`,
                        fontWeight: POLL.option.fontWeight,
                        fontSize: `${POLL.option.fontSize}px`,
                        lineHeight: `${POLL.option.lineHeight}px`,
                        color: o.textColor
                      }}
                    >
                      <span style={{ flexShrink: 0, ...rowSpanSize(draft.commandFontSize), ...dimOf(dimmed) }}>
                        {o.command !== '' ? o.command : `#${String.fromCharCode(65 + i)}`}
                      </span>
                      <span
                        style={{
                          flex: 1,
                          maxWidth: `${optionTextMaxWidth}px`,
                          wordBreak: 'break-word',
                          ...rowSpanSize(draft.displayTextFontSize),
                          ...dimOf(dimmed)
                        }}
                      >
                        {o.displayText.length > 0 ? o.displayText : `Option ${String.fromCharCode(65 + i)}`}
                      </span>
                      <span data-split-figures={i} style={{ flexShrink: 0, whiteSpace: 'nowrap', ...rowSpanSize(draft.numberFontSize) }}>
                        <VoteMark platform='twitch' height={SPLIT_LANES.figureMarkHeight} />
                        <span data-lane-pct='twitch' style={{ marginLeft: '5px', ...dimOf(dimmed) }}>{`${twitchPcts[i] ?? 0}%`}</span>
                        <VoteMark platform='youtube' height={SPLIT_LANES.figureMarkHeight} gapBefore={12} />
                        <span data-lane-pct='youtube' style={{ marginLeft: '5px', ...dimOf(dimmed) }}>{`${youtubePcts[i] ?? 0}%`}</span>
                      </span>
                    </div>
                    <div
                      style={{
                        marginTop: `${POLL.option.textToBarGap}px`,
                        marginLeft: 'auto',
                        width: `${barWidth + SPLIT_LANES.markColumn + SPLIT_LANES.markGap}px`,
                        maxWidth: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: `${SPLIT_LANES.laneGap}px`
                      }}
                    >
                      <VoteLane platform='twitch' pct={twitchPcts[i] ?? 0} color={o.progressBarColor} glint={correct} dimmed={dimmed} />
                      <VoteLane platform='youtube' pct={youtubePcts[i] ?? 0} color={o.progressBarColor} glint={correct} dimmed={dimmed} />
                    </div>
                  </div>
                )
              }
              return (
                <div
                  key={i}
                  style={{
                    position: 'relative',
                    background: rowBg,
                    color: draft.optionsTextColor,
                    paddingTop: `${POLL.option.paddingTop}px`,
                    paddingBottom: `${POLL.option.paddingBottom}px`,
                    paddingLeft: `${POLL.option.paddingLeft}px`,
                    paddingRight: `${POLL.option.paddingRight}px`,
                    opacity: revealed && correctIndex !== -1 && i !== correctIndex ? 0.45 : 1,
                    transition: 'opacity 350ms ease'
                  }}
                >
                  {revealed && i === correctIndex && (
                    <CorrectAnswerGlow color={draft.winnerOutlineColor} />
                  )}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: rowAlign,
                      justifyContent: 'space-between',
                      gap: `${POLL.option.chatCommandGap}px`,
                      fontWeight: POLL.option.fontWeight,
                      fontSize: `${POLL.option.fontSize}px`,
                      lineHeight: `${POLL.option.lineHeight}px`,
                      color: o.textColor
                    }}
                  >
                    <span style={{ flexShrink: 0, ...rowSpanSize(draft.commandFontSize) }}>
                      {o.command !== '' ? o.command : `#${String.fromCharCode(65 + i)}`}
                    </span>
                    <span
                      style={{
                        flex: 1,
                        maxWidth: `${optionTextMaxWidth}px`,
                        wordBreak: 'break-word',
                        ...rowSpanSize(draft.displayTextFontSize)
                      }}
                    >
                      {o.displayText.length > 0 ? o.displayText : `Option ${String.fromCharCode(65 + i)}`}
                    </span>
                    {!hiddenMode && <span style={{ flexShrink: 0, ...rowSpanSize(draft.numberFontSize) }}>{countLabel}</span>}
                  </div>
                  {!hiddenMode && (
                    <div
                      style={{
                        marginTop: `${POLL.option.textToBarGap}px`,
                        marginLeft: 'auto',
                        width: `${barWidth}px`,
                        height: `${POLL.option.barHeight}px`,
                        borderRadius: '9999px',
                        background: 'rgba(255,255,255,0.15)',
                        overflow: 'hidden'
                      }}
                    >
                      <div
                        style={{
                          position: 'relative',
                          width: `${filledWidth}px`,
                          height: '100%',
                          borderRadius: '9999px',
                          background: o.progressBarColor,
                          overflow: 'hidden',
                          transition: 'width 180ms ease-out'
                        }}
                      >
                        {revealed && i === correctIndex && (
                          <div
                            style={{
                              position: 'absolute',
                              inset: 0,
                              background: 'linear-gradient(110deg, transparent 35%, rgba(255,255,255,0.85) 50%, transparent 65%)',
                              backgroundSize: '220% 100%',
                              backgroundRepeat: 'no-repeat',
                              animation: 'spTriviaGlint 2s linear infinite'
                            }}
                          />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
          {hiddenMode && live?.answersIn != null && (
            <div
              style={{
                background: draft.options.length % 2 === 0 ? draft.optionsBackground : altBg,
                color: draft.optionsTextColor,
                paddingTop: '10px',
                paddingBottom: '12px',
                textAlign: 'center',
                fontWeight: POLL.option.fontWeight,
                fontSize: `${counterFontSize}px`,
                opacity: 0.92
              }}
            >
              {laneMode === 'YOUTUBE'
                ? `${live.answersIn.youtube} answer${live.answersIn.youtube === 1 ? '' : 's'} in`
                : live.answersIn.youtube > 0
                  ? `Answers in · Twitch ${live.answersIn.twitch} · YouTube ${live.answersIn.youtube}`
                  : `${live.answersIn.twitch} answer${live.answersIn.twitch === 1 ? '' : 's'} in`}
            </div>
          )}
          <div
            style={{
              minHeight: `${POLL.callToAction.minHeight}px`,
              paddingTop: `${POLL.callToAction.paddingTop}px`,
              paddingBottom: `${POLL.callToAction.paddingBottom}px`,
              background: draft.callToActionBackground,
              color: draft.callToActionTextColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {live?.revealed === true
              ? (
                <RevealPill
                  live={live}
                  correctIndex={correctIndex}
                  youtubeRemoved={live.youtubeRemoved}
                  fontWeight={elementPinnedWeight(draft.fontKey, 600)}
                  fontSize={pillFontSize}
                />
                )
              : answersLocked
                ? (
                  <AnswersLockedPill fontWeight={elementPinnedWeight(draft.fontKey, 600)} fontSize={pillFontSize} />
                  )
                : (draft.callToActionText.trim().length > 0 && (
                  <span
                    style={{
                      maxWidth: `${ctaTextMaxWidth}px`,
                      textAlign: 'center',
                      fontWeight: POLL.callToAction.fontWeight,
                      fontSize: `${draft.callToActionFontSize ?? POLL.callToAction.fontSize}px`
                    }}
                  >
                    {draft.callToActionText}
                  </span>
                  ))}
          </div>
        </div>
      </div>
    </CenteredOnCanvas>
  )
}
